<template>
    <div>
        <!-- Overlay Backdrop -->
        <div class="recent-searches-backdrop" :class="{ show: show }" @click="close"></div>

        <!-- Recent Searches Sidebar -->
        <div class="recent-searches-sidebar" :class="{ 'sidebar-collapsed': !show }">
            <!-- Sidebar Header -->
            <div class="sidebar-header">
                <h3 class="sidebar-title">Recent Searches</h3>
                <button @click="close" class="sidebar-toggle-btn" title="Close sidebar">
                    <FontAwesomeIcon :icon="faXmark" />
                </button>
            </div>

            <!-- Search Tabs -->
            <div class="search-tabs">
                <el-tabs v-model="activeTab" @tab-change="handleTabChange">
                    <el-tab-pane label="" name="recent">
                        <div class="search-list-container">
                            <div v-if="isLoadingRecent" class="loading-history">
                                <font-awesome-icon :icon="['fas', 'spinner']" spin />
                                <p class="mt-3">Loading recent searches...</p>
                            </div>
                            <div v-else-if="!hasAnyRecentSearches" class="empty-history">
                                <font-awesome-icon :icon="['fas', 'search']" />
                                <span>No recent searches found</span>
                            </div>
                            <div v-else class="search-list">
                                <!-- Today -->
                                <div v-if="recentSearches.today?.length" class="search-group">
                                    <h3 class="group-title">Today</h3>
                                    <div v-for="search in recentSearches.today" :key="search.id" class="search-item"
                                        @click="selectSearch(search)">
                                        <div class="search-item-content">
                                            <div class="search-item-left">
                                                <i class="fas fa-search search-item-icon"></i>
                                                <div class="search-item-title">
                                                    <a class="link-title" :href="search.url || 'javascript:void(0)'">
                                                        <img :src="searchImg" class="search-icon" />
                                                        {{
                                                            search.saved_name ||
                                                            `Search Results for "${search.query}"`
                                                        }} <span v-if="search.count > 1">({{ search.count }})</span>
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="search-item-actions">
                                            <button class="action-btn" @click.stop="copyLink(search)" title="Copy link">
                                                <img :src="linkImg" class="link-img" />
                                            </button>
                                            <button class="action-btn" @click.stop="toggleSave(search)"
                                                title="Save search">
                                                <img :src="saveImg" class="save-icon" :class="{
                                                    saved: search.is_saved,
                                                }" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Yesterday -->
                                <div v-if="recentSearches.yesterday?.length" class="search-group">
                                    <h3 class="group-title">Yesterday</h3>
                                    <div v-for="search in recentSearches.yesterday" :key="search.id" class="search-item"
                                        @click="selectSearch(search)">
                                        <div class="search-item-content">
                                            <div class="search-item-left">
                                                <i class="fas fa-search search-item-icon"></i>
                                                <div class="search-item-title">
                                                    <a class="link-title" :href="search.url || 'javascript:void(0)'">
                                                        <img :src="searchImg" class="search-icon" />
                                                        {{
                                                            search.saved_name ||
                                                            `Search Results for "${search.query}"`
                                                        }}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="search-item-actions">
                                            <button class="action-btn" @click.stop="copyLink(search)" title="Copy link">
                                                <img :src="linkImg" class="link-img" />
                                            </button>
                                            <button class="action-btn" @click.stop="toggleSave(search)"
                                                title="Save search">
                                                <img :src="saveImg" class="save-icon" :class="{
                                                    saved: search.is_saved,
                                                }" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Last 7 Days -->
                                <div v-if="recentSearches.last_7_days?.length" class="search-group">
                                    <h3 class="group-title">Last 7 Days</h3>
                                    <div v-for="search in recentSearches.last_7_days" :key="search.id"
                                        class="search-item" @click="selectSearch(search)">
                                        <div class="search-item-content">
                                            <div class="search-item-left">
                                                <i class="fas fa-search search-item-icon"></i>
                                                <div class="search-item-title">
                                                    <a class="link-title" :href="search.url || 'javascript:void(0)'">
                                                        <img :src="searchImg" class="search-icon" />
                                                        {{
                                                            search.saved_name ||
                                                            `Search Results for "${search.query}"`
                                                        }}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="search-item-actions">
                                            <button class="action-btn" @click.stop="copyLink(search)" title="Copy link">
                                                <img :src="linkImg" class="link-img" />
                                            </button>
                                            <button class="action-btn" @click.stop="toggleSave(search)"
                                                title="Save search">
                                                <img :src="saveImg" class="save-icon" :class="{
                                                    saved: search.is_saved,
                                                }" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Last Month -->
                                <div v-if="recentSearches.last_month?.length" class="search-group">
                                    <h3 class="group-title">Last Month</h3>
                                    <div v-for="search in recentSearches.last_month" :key="search.id"
                                        class="search-item" @click="selectSearch(search)">
                                        <div class="search-item-content">
                                            <div class="search-item-left">
                                                <i class="fas fa-search search-item-icon"></i>
                                                <div class="search-item-title">
                                                    <a class="link-title" :href="search.url || 'javascript:void(0)'">
                                                        <img :src="searchImg" class="search-icon" />
                                                        {{
                                                            search.saved_name ||
                                                            `Search Results for "${search.query}"`
                                                        }}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="search-item-actions">
                                            <button class="action-btn" @click.stop="copyLink(search)" title="Copy link">
                                                <img :src="linkImg" class="link-img" />
                                            </button>
                                            <button class="action-btn" @click.stop="toggleSave(search)"
                                                title="Save search">
                                                <img :src="saveImg" class="save-icon" :class="{
                                                    saved: search.is_saved,
                                                }" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Older -->
                                <div v-if="
                                    Object.keys(recentSearches.older || {})
                                        .length
                                " class="search-group">
                                    <template v-for="(
searches, month
                                        ) in recentSearches.older" :key="month">
                                        <h3 class="group-title">{{ month }}</h3>
                                        <div v-for="search in searches" :key="search.id" class="search-item"
                                            @click="selectSearch(search)">
                                            <div class="search-item-content">
                                                <div class="search-item-left">
                                                    <font-awesome-icon :icon="[
                                                        'fas',
                                                        'search',
                                                    ]" class="search-item-icon" />
                                                    <div class="search-item-title">
                                                        <a class="link-title"
                                                            :href="search.url || 'javascript:void(0)'">
                                                            <img :src="searchImg" class="search-icon" />
                                                            {{
                                                                search.saved_name ||
                                                                `Search Results for "${search.query}"`
                                                            }}
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="search-item-actions">
                                                <button class="action-btn" @click.stop="
                                                    copyLink(search)
                                                    " title="Copy link">
                                                    <img :src="linkImg" class="link-img" />
                                                </button>
                                                <button class="action-btn" @click.stop="
                                                    toggleSave(search)
                                                    " title="Save search">
                                                    <img :src="saveImg" class="save-icon" :class="{
                                                        saved: search.is_saved,
                                                    }" />
                                                </button>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </el-tab-pane>

                    <el-tab-pane v-if="false" label="Saved" name="saved">
                        <div class="search-list-container">
                            <div v-if="isLoadingSaved" class="loading-history">
                                <font-awesome-icon :icon="['fas', 'spinner']" spin />
                                <span>Loading saved searches...</span>
                            </div>
                            <div v-else-if="!hasAnySavedSearches" class="empty-history">
                                <img :src="saveImg" class="save-icon" />
                                <span>No saved searches found</span>
                            </div>
                            <div v-else class="search-list">
                                <!-- Today -->
                                <div v-if="savedSearches.today?.length" class="search-group">
                                    <h3 class="group-title">Today</h3>
                                    <div v-for="search in savedSearches.today" :key="search.id"
                                        class="search-item saved-item" @click="selectSearch(search)">
                                        <div class="search-item-content">
                                            <div class="search-item-title">
                                                <img :src="searchImg" class="search-icon" />
                                                {{ search.name }}
                                            </div>
                                        </div>
                                        <div class="search-item-arrow">
                                            <font-awesome-icon :icon="['fas', 'chevron-right']" />
                                        </div>
                                    </div>
                                </div>

                                <!-- Yesterday -->
                                <div v-if="savedSearches.yesterday?.length" class="search-group">
                                    <h3 class="group-title">Yesterday</h3>
                                    <div v-for="search in savedSearches.yesterday" :key="search.id"
                                        class="search-item saved-item" @click="selectSearch(search)">
                                        <div class="search-item-content">
                                            <div class="search-item-title">
                                                <img :src="searchImg" class="search-icon" />
                                                {{ search.name }}
                                            </div>
                                        </div>
                                        <div class="search-item-arrow">
                                            <font-awesome-icon :icon="['fas', 'chevron-right']" />
                                        </div>
                                    </div>
                                </div>

                                <!-- Last 7 Days -->
                                <div v-if="savedSearches.last_7_days?.length" class="search-group">
                                    <h3 class="group-title">Last 7 Days</h3>
                                    <div v-for="search in savedSearches.last_7_days" :key="search.id"
                                        class="search-item saved-item" @click="selectSearch(search)">
                                        <div class="search-item-content">
                                            <div class="search-item-title">
                                                <img :src="searchImg" class="search-icon" />
                                                {{ search.name }}
                                            </div>
                                        </div>
                                        <div class="search-item-arrow">
                                            <font-awesome-icon :icon="['fas', 'chevron-right']" />
                                        </div>
                                    </div>
                                </div>

                                <!-- Last Month -->
                                <div v-if="savedSearches.last_month?.length" class="search-group">
                                    <h3 class="group-title">Last Month</h3>
                                    <div v-for="search in savedSearches.last_month" :key="search.id"
                                        class="search-item saved-item" @click="selectSearch(search)">
                                        <div class="search-item-content">
                                            <div class="search-item-title">
                                                <img :src="searchImg" class="search-icon" />
                                                {{ search.name }}
                                            </div>
                                        </div>
                                        <div class="search-item-arrow">
                                            <font-awesome-icon :icon="['fas', 'chevron-right']" />
                                        </div>
                                    </div>
                                </div>

                                <!-- Older -->
                                <div v-if="
                                    Object.keys(savedSearches.older || {})
                                        .length
                                " class="search-group">
                                    <template v-for="(
searches, month
                                        ) in savedSearches.older" :key="month">
                                        <h3 class="group-title">{{ month }}</h3>
                                        <div v-for="search in searches" :key="search.id" class="search-item saved-item"
                                            @click="selectSearch(search)">
                                            <div class="search-item-content">
                                                <div class="search-item-title">
                                                    <img :src="searchImg" class="search-icon" />
                                                    {{ search.name }}
                                                </div>
                                            </div>
                                            <div class="search-item-arrow">
                                                <i class="fas fa-chevron-right"></i>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </el-tab-pane>
                </el-tabs>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { ElNotification } from "element-plus";
import Swal from "sweetalert2";
import RecentSearchesApi from "@api/recentSearches.api.js";
import linkImg from "@/assets/img/link-01.png";
import searchImg from "@/assets/img/search-01.png";
import saveImg from "@/assets/img/save.png";
import { useRoute, useRouter } from "vue-router";

const router = useRouter();

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["close", "search-selected"]);

const activeTab = ref("recent");
const recentSearches = ref({
    today: [],
    yesterday: [],
    last_7_days: [],
    last_month: [],
    older: {},
});
const savedSearches = ref({
    today: [],
    yesterday: [],
    last_7_days: [],
    last_month: [],
    older: {},
});

const isLoadingRecent = ref(false);
const isLoadingSaved = ref(false);

const hasAnyRecentSearches = computed(() => {
    const result =
        recentSearches.value.today?.length ||
        recentSearches.value.yesterday?.length ||
        recentSearches.value.last_7_days?.length ||
        recentSearches.value.last_month?.length ||
        Object.keys(recentSearches.value.older || {}).length;
    console.log("hasAnyRecentSearches computed:", result);
    console.log("recentSearches.value:", recentSearches.value);
    return result;
});

const hasAnySavedSearches = computed(() => {
    return (
        savedSearches.value.today?.length ||
        savedSearches.value.yesterday?.length ||
        savedSearches.value.last_7_days?.length ||
        savedSearches.value.last_month?.length ||
        Object.keys(savedSearches.value.older || {}).length
    );
});

watch(
    () => props.show,
    (newVal) => {
        if (newVal) {
            fetchData();
        }
    },
);

const fetchData = async () => {
    await Promise.all([fetchRecentSearches(), fetchSavedSearches()]);
};

const fetchRecentSearches = async () => {
    isLoadingRecent.value = true;
    try {
        const response = await RecentSearchesApi.getRecentSearches({
            limit: 100,
        });
        console.log("Recent searches API response:", response);
        if (response.data) {
            console.log("Assigning recent searches:", response.data.data);
            recentSearches.value = response.data.data;
            console.log(
                "Recent searches after assignment:",
                recentSearches.value,
            );
        }
    } catch (error) {
        console.error("Error fetching recent searches:", error);
        ElMessage.error("Failed to load recent searches");
    } finally {
        isLoadingRecent.value = false;
    }
};

const fetchSavedSearches = async () => {
    // isLoadingSaved.value = true
    // try {
    //     const response = await RecentSearchesApi.getSavedSearches({ limit: 50 })
    //     if (response.data) {
    //         savedSearches.value = response.data.data
    //     }
    // } catch (error) {
    //     console.error('Error fetching saved searches:', error)
    //     ElMessage.error('Failed to load saved searches')
    // } finally {
    //     isLoadingSaved.value = false
    // }
};

const handleTabChange = (tabName) => {
    activeTab.value = tabName;
};

const close = () => {
    emit("close");
};

const selectSearch = (search) => {
    return false;
    const query = search.query || search.search_query;
    emit("search-selected", query);
    close();
};

const copyLink = (search) => {
    const query = search.query || search.search_query;
    const url = `${window.location.origin}/search?Keyword=${encodeURIComponent(query)}&currentTab=project`;
    navigator.clipboard
        .writeText(url)
        .then(() => {
            ElNotification({
                title: "Success",
                message: "Link copied to clipboard",
                type: "success",
                position: "bottom-right",
            });
        })
        .catch(() => {
            ElNotification({
                title: "Error",
                message: "Failed to copy link",
                type: "error",
                position: "bottom-right",
            });
        });
};

const toggleSave = async (search) => {
    try {
        if (search.is_saved) {
            // Show confirmation dialog before unsaving
            const result = await Swal.fire({
                title: "Remove Saved Search",
                text: "Do you want to remove this saved search?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Yes, remove it!",
                cancelButtonText: "Cancel",
                reverseButtons: true,
            });

            if (result.isConfirmed) {
                // Unsave the search
                await RecentSearchesApi.deleteSavedSearch({
                    model_log_id: search.id,
                });
                search.is_saved = false;
                search.saved_name = null;
                ElNotification({
                    title: "Success",
                    message: "Search removed from saved",
                    type: "success",
                    position: "bottom-right",
                });
            }
        } else {
            // Show confirmation dialog before saving
            const result = await Swal.fire({
                title: "Save Search",
                text: "Do you want to save this?",
                icon: "question",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, save it!",
                cancelButtonText: "Cancel",
                reverseButtons: true,
            });

            if (result.isConfirmed) {
                // Save the search
                await RecentSearchesApi.saveSearch({ model_log_id: search.id });
                search.is_saved = true;
                ElNotification({
                    title: "Success",
                    message: "Search saved successfully",
                    type: "success",
                    position: "bottom-right",
                });
                // Refresh data to update UI
                await fetchData();
            }
        }
    } catch (error) {
        console.error("Error toggling save:", error);
        ElNotification({
            title: "Error",
            message: "Failed to update search",
            type: "error",
            position: "bottom-right",
        });
    }
};

function openLink(url) {
    window.location.href = url;
}

</script>

<style scoped>
.search-icon {
    width: 20px;
    height: 20px;
    margin-right: 3px;
}

.saved {
    filter: invert(83%) sepia(58%) saturate(6000%) hue-rotate(2deg) brightness(107%) contrast(102%);
}

.save-icon {
    width: 20px;
    height: auto;
}

.link-img {
    width: 20px;
    height: 20px;
}

/* Recent Searches Sidebar - Mirror of Chat Sidebar but on the right */
.recent-searches-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 100000;
    opacity: 0;
    visibility: hidden;
    transition:
        opacity 0.3s ease,
        visibility 0.3s ease;
}

.recent-searches-backdrop.show {
    opacity: 1;
    visibility: visible;
}

.recent-searches-sidebar {
    position: fixed;
    top: 0;
    right: 0;
    width: auto;
    height: 100%;
    background: white;
    box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15);
    z-index: 100001;
    transform: translateX(100%);
    transition: transform 0.3s ease;
    display: flex;
    flex-direction: column;
    padding: 10px;
    overflow-x: scroll;
}

@media (max-width: 468px) {
    .recent-searches-sidebar {
        width: 100%;
    }
}

.recent-searches-sidebar.sidebar-collapsed {
    transform: translateX(100%);
}

.recent-searches-sidebar:not(.sidebar-collapsed) {
    transform: translateX(0);
}

/* Use same styles as chat sidebar */
.sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #f0f0f0;
    margin-bottom: 20px;
}

.sidebar-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #46454d;
}

.sidebar-toggle-btn {
    background: none;
    border: none;
    padding: 8px;
    border-radius: 6px;
    cursor: pointer;
    color: #666;
    transition: background-color 0.2s;
}

.sidebar-toggle-btn:hover {
    background: #e9ecef;
}

.search-tabs {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.search-tabs :deep(.el-tabs) {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.search-tabs :deep(.el-tabs__content) {
    flex: 1;
    overflow: hidden;
}

.search-tabs :deep(.el-tab-pane) {
    height: 100%;
    overflow: hidden;
}

.search-list-container {
    height: 100%;
    overflow-y: auto;
    padding: 0;
}

.loading-history,
.empty-history {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: #666;
}

.loading-history i,
.empty-history i {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
}

.search-group:first-child {
    margin-top: 1px;
    border-radius: 10px 10px 0 0;
}

.search-group {
    margin-top: -5px;
    border: 1px solid #f0f0f0;
    overflow-x: scroll;
    margin-left: 4px;
    margin-right: 2px;
    color: rgb(94, 69, 255);
}

.group-title {
    font-size: 18px;
    font-weight: 500;
    color: #281a8b;
    margin: 0 0 12px 0;
    padding: 20px;
    background: #fbfaff !important;
}

.search-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.search-item {
    display: flex;
    align-items: center;
    padding: 12px 20px;
    cursor: pointer;
    transition: background-color 0.2s;
    border-left: 3px solid transparent;
}

.search-item:hover {
    background-color: #f8f9fa;
}

.search-item.saved-item:hover {
    background-color: #fff3cd;
    border-left-color: #ffc107;
}

.search-item-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1;
    min-width: 0;
}

.search-item-left {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
}

.search-item-icon {
    color: #666;
    font-size: 14px;
    margin-right: 12px;
    flex-shrink: 0;
}

.search-item-title {
    font-size: 14px;
    color: #333;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
}

.search-item-date {
    font-size: 12px;
    color: #666;
    margin-top: 2px;
}

.search-item-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-shrink: 0;
}

.action-btn {
    background: none;
    border: none;
    padding: 6px 8px;
    border-radius: 4px;
    cursor: pointer;
    color: #666;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.action-btn:hover {
    background: #f0f0f0;
    color: #007bff;
}

.action-btn i.saved {
    color: #ffc107;
}

.action-btn i.saved:hover {
    color: #856404;
}

.search-item-arrow {
    color: #ccc;
    font-size: 12px;
    transition: color 0.2s;
}

.search-item:hover .search-item-arrow {
    color: #007bff;
}

.search-item.saved-item:hover .search-item-arrow {
    color: #856404;
}

:deep(.el-tabs__nav-scroll) {
    display: none !important;
}

/* Tab styling to match design */
.search-tabs :deep(.el-tabs__header) {
    margin: 0;
    background: #fff;
}

.search-tabs :deep(.el-tabs__nav-wrap) {
    padding: 0 20px;
}

.search-tabs :deep(.el-tabs__item) {
    padding: 0 16px;
    height: 48px;
    line-height: 48px;
    font-weight: 500;
    color: #666;
}

.search-tabs :deep(.el-tabs__item.is-active) {
    color: #007bff;
    font-weight: 600;
}

.search-tabs :deep(.el-tabs__active-bar) {
    background-color: #007bff;
}
</style>
